import styled from '@emotion/styled';
import {
  Dialog,
  Box,
  Typography,
  Tab,
  Tabs,
  Select,
  Button,
} from '@mui/material';

// Main Dialog Container
export const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    position: fixed;
    right: 0;
    top: 100px;
    bottom: 0;
    width: 900px;
    height: auto;
    margin: 0;
    max-width: none;
    max-height: none;
    border-radius: 0;
    background: var(--bg-color);
    display: flex;
    flex-direction: column;
    background-color: var(--bg-color);
  }
`;

// Fixed Header Wrapper
export const FixedHeaderWrapper = styled(Box)`
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 10;
  padding: 15px 32px 0;
  flex-shrink: 0;
`;

// Scrollable Content Wrapper
export const ContentWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 32px 20px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

// Header Section
export const HeaderSection = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 20px;
  flex-shrink: 0;
`;

export const HeaderLeft = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const TitleTypography = styled(Typography)`
  font-family: 'Lufga', sans-serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 100%;
  color: #0f52ba;
`;

export const CloseIcon = styled(Box)`
  width: 24px;
  height: 24px;
  color: #75838a;
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: #0b2330;
  }
`;

// Modern Tabs Styling
export const StyledTabs = styled(Tabs)`
  min-height: 44px;
  border-bottom: 1px solid #e0e0e0;

  & .MuiTabs-indicator {
    height: 3px;
    border-radius: 3px 3px 0 0;
    background: linear-gradient(90deg, #0f52ba 0%, #1976d2 100%);
  }

  & .MuiTabs-flexContainer {
    gap: 8px;
  }
`;

export const StyledTab = styled(Tab)<{ selected?: boolean }>`
  font-family: 'Outfit', sans-serif;
  font-size: 14px;
  font-weight: ${({ selected }) => (selected ? '600' : '400')};
  color: ${({ selected }) => (selected ? '#0f52ba' : '#75838a')};
  text-transform: none;
  min-height: 44px;
  padding: 12px 20px;
  border-radius: 8px 8px 0 0;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #0f52ba;
    background-color: rgba(15, 82, 186, 0.04);
  }

  &.Mui-selected {
    color: #0f52ba;
    background-color: rgba(15, 82, 186, 0.08);
  }
`;

// Tab Panel Container
export const TabPanelContainer = styled(Box)`
  padding: 0;
`;

// Details Table Styling
export const DetailsTable = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e8ecef;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

export const DetailsRow = styled(Box)<{ isLast?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: ${({ isLast }) => (isLast ? 'none' : '1px solid #f0f2f4')};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f8fafc;
  }
`;

export const DetailsCell = styled(Box)<{ isLabel?: boolean }>`
  padding: 16px 20px;
  display: flex;
  align-items: center;
  font-family: 'Outfit', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ isLabel }) => (isLabel ? '#75838a' : '#0b2330')};
  font-weight: ${({ isLabel }) => (isLabel ? '400' : '500')};
  background-color: ${({ isLabel }) => (isLabel ? '#fafbfc' : 'transparent')};
  ${({ isLabel }) => (isLabel ? 'border-right: 1px solid #f0f2f4;' : '')}
`;

export const StatusBadge = styled(Box)<{ isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-family: 'Outfit', sans-serif;
  font-size: 12px;
  font-weight: 500;
  background-color: ${({ isActive }) =>
    isActive ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'};
  color: ${({ isActive }) => (isActive ? '#2e7d32' : '#c62828')};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${({ isActive }) => (isActive ? '#4caf50' : '#f44336')};
  }
`;

// Empty Logs Container
export const EmptyLogsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: #fafbfc;
  border-radius: 12px;
  border: 1px dashed #e0e0e0;
`;

export const EmptyLogsIcon = styled(Box)`
  font-size: 48px;
  color: #c1c1c1;
  margin-bottom: 16px;
`;

export const EmptyLogsText = styled(Typography)`
  font-family: 'Outfit', sans-serif;
  font-size: 14px;
  color: #75838a;
`;

// Logs Filter Section
export const LogsFilterContainer = styled(Box)`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 15px;
  flex-wrap: wrap;
`;

export const FilterField = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FilterLabel = styled(Typography)`
  font-family: 'Outfit', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #75838a;
`;

export const LogTypeSelect = styled(Select)`
  min-width: 160px;
  height: 30px;
  background-color: #ffffff;
  border-radius: 8px;
  font-family: 'Outfit', sans-serif;
  font-size: 12px;

  & .MuiSelect-select {
    padding: 10px 14px;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: #e0e0e0;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: #0f52ba;
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #0f52ba;
    border-width: 1px;
  }
`;

export const FilterButtonsContainer = styled(Box)`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: auto;
`;

export const ApplyButton = styled(Button)`
  height: 30px;
  padding: 0 20px;
  background-color: #0f52ba;
  color: #ffffff;
  font-family: 'Outfit', sans-serif;
  font-size: 12px;
  font-weight: 500;
  text-transform: none;
  border-radius: 8px;

  &:hover {
    background-color: #0d47a1;
  }
`;

export const ClearButton = styled(Button)`
  height: 30px;
  padding: 0 20px;
  background-color: transparent;
  color: #75838a;
  font-family: 'Outfit', sans-serif;
  font-size: 12px;
  font-weight: 500;
  text-transform: none;
  border-radius: 8px;
  border: 1px solid #e0e0e0;

  &:hover {
    background-color: #f5f5f5;
    border-color: #c1c1c1;
  }
`;

// Logs Grid Container
export const LogsGridContainer = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 300px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e8ecef;
  overflow: hidden;
`;

export const LogsDataGridWrapper = styled(Box)`
  flex: 1;
  width: 100%;

  & .MuiDataGrid-root {
    border: none;
  }

  & .MuiDataGrid-columnHeaders {
    background-color: #fafbfc;
    font-family: 'Outfit', sans-serif;
    font-weight: 500;
    font-size: 13px;
    color: #75838a;
    min-height: 48px !important;
  }

  & .MuiDataGrid-columnHeader {
    background-color: #fafbfc;
  }

  & .MuiDataGrid-columnSeparator {
    display: none;
  }

  & .MuiDataGrid-cell {
    font-family: 'Outfit', sans-serif;
    font-size: 14px;
    color: #0b2330;
    border-bottom: 1px solid #f0f2f4;
  }

  & .MuiDataGrid-row:hover {
    background-color: #f8fafc;
  }

  & .MuiDataGrid-footerContainer {
    display: none;
  }

  & .MuiDataGrid-cell:focus,
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-columnHeader:focus,
  & .MuiDataGrid-columnHeader:focus-within {
    outline: none;
  }
`;

export const LogTypeBadge = styled(Box)<{ logType?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 16px;
  font-family: 'Outfit', sans-serif;
  font-size: 12px;
  height: 16px;
  font-weight: 500;
  background-color: ${({ logType }) => {
    switch (logType) {
      case 'Received':
        return 'rgba(76, 175, 80, 0.1)';
      case 'Production':
        return 'rgba(33, 150, 243, 0.1)';
      case 'Production Update':
        return 'rgba(156, 39, 176, 0.1)';
      case 'Production Delete':
        return 'rgba(244, 67, 54, 0.1)';
      case 'Reset':
        return 'rgba(158, 158, 158, 0.1)';
      default:
        return 'rgba(158, 158, 158, 0.1)';
    }
  }};
  color: ${({ logType }) => {
    switch (logType) {
      case 'Received':
        return '#2e7d32';
      case 'Production':
        return '#1565c0';
      case 'Production Update':
        return '#7b1fa2';
      case 'Production Delete':
        return '#c62828';
      case 'Reset':
        return '#616161';
      default:
        return '#616161';
    }
  }};
`;

export const QuantityChange = styled('span')<{ isPositive?: boolean }>`
  font-family: 'Outfit', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ isPositive }) => (isPositive ? '#2e7d32' : '#c62828')};
`;

// Date Filter Error Message
export const DateFilterErrorText = styled(Typography)`
  font-family: 'Outfit', sans-serif;
  font-size: 11px;
  color: #d32f2f;
  margin-top: 52px;
  margin-left: 2px;
  position: absolute;
`;

export const ReferenceCellContainer = styled(Box)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
